import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Review {
  id: string;
  customer_name: string;
  location_from: string | null;
  location_to: string | null;
  review_text: string;
  rating: number;
  created_at: string;
}

interface Testimonial {
  name: string;
  location: string;
  text: string;
  rating: number;
  saved: string;
  hoursAgo?: number;
}

const calculateHoursAgo = (createdAt: string): number => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60));
};

const generateSavedAmount = (): string => {
  // Generate a realistic savings amount between €150 and €450
  const amount = Math.floor(Math.random() * 300) + 150;
  return `€${amount}`;
};

export const useReviews = (limit: number = 12) => {
  return useQuery({
    queryKey: ["homepage-reviews", limit],
    queryFn: async (): Promise<Testimonial[]> => {
      const { data, error } = await supabase
        .from("reviews")
        .select("id, customer_name, location_from, location_to, review_text, rating, created_at")
        .eq("published", true)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("Error fetching reviews:", error);
        throw error;
      }

      return (data || []).map((review: Review) => ({
        name: review.customer_name,
        location: review.location_from && review.location_to 
          ? `${review.location_from} → ${review.location_to}`
          : review.location_from || review.location_to || "Nederland",
        text: review.review_text,
        rating: review.rating,
        saved: generateSavedAmount(),
        hoursAgo: calculateHoursAgo(review.created_at),
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
